'use client';

import { useEffect, useCallback, useRef } from 'react';
import { useSocket, WsNewMessage, WsUserTyping, WsUserStatus, WsMessageRead, WsMessageEdited, WsMessageDeleted, WsMessageReaction } from './SocketContext';

interface UseMessengerSocketOptions {
  // Текущий выбранный пользователь в DM
  selectedUserId?: number | null;
  // Текущая выбранная комната
  selectedRoomId?: number | null;
  // Мой userId
  myUserId?: number | null;
  // Callbacks
  onNewMessage?: (data: WsNewMessage) => void;
  onTyping?: (data: WsUserTyping) => void;
  onUserStatus?: (data: WsUserStatus) => void;
  onMessageRead?: (data: WsMessageRead) => void;
  onMessageEdited?: (data: WsMessageEdited) => void;
  onMessageDeleted?: (data: WsMessageDeleted) => void;
  onReaction?: (data: WsMessageReaction) => void;
}

export function useMessengerSocket(options: UseMessengerSocketOptions) {
  const {
    selectedUserId,
    selectedRoomId,
    myUserId,
    onNewMessage,
    onTyping,
    onUserStatus,
    onMessageRead,
    onMessageEdited,
    onMessageDeleted,
    onReaction,
  } = options;

  const {
    isConnected,
    sendTyping,
    joinRoom,
    leaveRoom,
    markRead,
    onNewMessage: subscribeNewMessage,
    onUserTyping: subscribeTyping,
    onUserStatus: subscribeStatus,
    onMessageRead: subscribeMessageRead,
    onMessageEdited: subscribeMessageEdited,
    onMessageDeleted: subscribeMessageDeleted,
    onMessageReaction: subscribeReaction,
  } = useSocket();

  // Refs для текущих значений (избегаем пересоздания эффектов)
  const selectedUserIdRef = useRef(selectedUserId);
  const selectedRoomIdRef = useRef(selectedRoomId);
  selectedUserIdRef.current = selectedUserId;
  selectedRoomIdRef.current = selectedRoomId;

  // Подписка на новые сообщения
  useEffect(() => {
    if (!onNewMessage) return;
    return subscribeNewMessage((data) => {
      // Фильтруем только релевантные сообщения
      if (data.type === 'dm' || data.type === 'dm_sent') {
        // DM сообщение для текущего диалога
        const fromId = data.fromUserId;
        const toId = data.toUserId;
        const currentUser = selectedUserIdRef.current;
        if (currentUser && (fromId === currentUser || toId === currentUser)) {
          onNewMessage(data);
        } else {
          // Сообщение для другого диалога — можно показать уведомление
          onNewMessage(data);
        }
      } else if (data.type === 'room') {
        // Сообщение в комнату
        if (data.roomId === selectedRoomIdRef.current) {
          onNewMessage(data);
        } else {
          onNewMessage(data);
        }
      }
    });
  }, [subscribeNewMessage, onNewMessage]);

  // Подписка на typing
  useEffect(() => {
    if (!onTyping) return;
    return subscribeTyping(onTyping);
  }, [subscribeTyping, onTyping]);

  // Подписка на статус пользователей
  useEffect(() => {
    if (!onUserStatus) return;
    return subscribeStatus(onUserStatus);
  }, [subscribeStatus, onUserStatus]);

  // Подписка на прочтение
  useEffect(() => {
    if (!onMessageRead) return;
    return subscribeMessageRead(onMessageRead);
  }, [subscribeMessageRead, onMessageRead]);

  // Подписка на редактирование
  useEffect(() => {
    if (!onMessageEdited) return;
    return subscribeMessageEdited(onMessageEdited);
  }, [subscribeMessageEdited, onMessageEdited]);

  // Подписка на удаление
  useEffect(() => {
    if (!onMessageDeleted) return;
    return subscribeMessageDeleted(onMessageDeleted);
  }, [subscribeMessageDeleted, onMessageDeleted]);

  // Подписка на реакции
  useEffect(() => {
    if (!onReaction) return;
    return subscribeReaction(onReaction);
  }, [subscribeReaction, onReaction]);

  // Автоматический join/leave комнаты
  useEffect(() => {
    if (!selectedRoomId) return;
    joinRoom(selectedRoomId);
    return () => leaveRoom(selectedRoomId);
  }, [selectedRoomId, joinRoom, leaveRoom]);

  // Отправка typing
  const emitTyping = useCallback((isTyping: boolean) => {
    if (selectedUserId) {
      sendTyping(selectedUserId, undefined, isTyping);
    } else if (selectedRoomId) {
      sendTyping(undefined, selectedRoomId, isTyping);
    }
  }, [selectedUserId, selectedRoomId, sendTyping]);

  // Отправка прочтения
  const emitMarkRead = useCallback((messageId: number) => {
    if (selectedUserId) {
      markRead(selectedUserId, messageId);
    }
  }, [selectedUserId, markRead]);

  return {
    isConnected,
    emitTyping,
    emitMarkRead,
  };
}
